<template>
  <b-modal v-model="active"
           :title="computedTitle"
           size="lg"
           centered
           scrollable
           ok-only
           ok-title="Cerrar"
           no-close-on-backdrop
           no-close-on-esc
           :hide-header-close="costDirty"
           @ok.prevent="handleOk"
           @hidden="onHidden">

    <div v-if="loading" class="text-center my-4">
      <b-spinner class="align-middle mr-2"></b-spinner>
      <strong>Cargando precios...</strong>
    </div>

    <template v-else>
      <div v-if="product" class="ppe-product-bar mb-3">
        <b-icon icon="box-seam" class="mr-2 text-muted flex-shrink-0"></b-icon>
        <div>
          <div class="font-weight-bold">{{ product.name }}</div>
          <small class="text-muted">{{ product.code }}</small>
        </div>
      </div>

      <b-alert show variant="warning" class="mb-3 ppe-link-alert" v-if="supplierNotLinked">
        <div class="ppe-link-alert__body">
          <div>
            <strong>Este proveedor no tiene este producto en su lista de costos.</strong>
            <div class="small mt-1">Vinculalo para guardar precios de compra y venta desde acá. El precio del ítem se aplica igual.</div>
          </div>
          <b-button variant="primary" size="sm" :disabled="linking" @click="linkSupplierToProduct" class="flex-shrink-0">
            <b-spinner small v-if="linking" class="mr-1"></b-spinner>
            <b-icon icon="link45deg" v-else class="mr-1"></b-icon>
            Vincular proveedor
          </b-button>
        </div>
      </b-alert>
      <b-alert show variant="info" class="mb-3" v-else-if="product && !supplierProduct && !supplierId">
        Sin proveedor vinculado en costos. El costo se aplica al ítem pero no se guarda en la ficha.
      </b-alert>

      <template v-if="!supplierNotLinked">

      <div v-if="supplierProduct" class="ppe-supplier-bar mb-2">
        <b-icon icon="truck" class="mr-1 text-muted flex-shrink-0"></b-icon>
        <span class="small text-muted">{{ supplierProduct.supplier_name }}</span>
      </div>

      <!-- COSTO DE COMPRA -->
      <div class="ppe-panel mb-3">
      <div class="ppe-panel-title mb-2">
        <b-icon icon="cart-check" scale="0.9" class="mr-1"></b-icon>
        <span>Costo de compra</span>
        <b-badge v-if="costSaved" variant="success" class="ml-2 ppe-saved-badge">Guardado</b-badge>
        <b-badge v-if="costDirty && !costSaved" variant="warning" class="ml-2 ppe-saved-badge">Sin guardar</b-badge>
        <b-form-checkbox
          v-model="priceWithIva"
          switch
          size="sm"
          class="mb-0 ml-auto ppe-iva-toggle"
          @change="onPriceWithIvaToggle"
        >c/IVA</b-form-checkbox>
      </div>

      <b-form-group class="mb-2">
        <b-input-group prepend="$" size="lg">
          <formatted-number-input
            v-if="!priceWithIva"
            v-model="form.price_list"
            size="lg"
            @input="onCostChanged"
          />
          <formatted-number-input
            v-else
            v-model="form.price_list_iva"
            size="lg"
            @input="onPriceListIvaInput"
          />
          <b-input-group-append is-text>
            <small class="text-muted">{{ priceWithIva ? 'c/IVA' : 's/IVA' }}</small>
          </b-input-group-append>
        </b-input-group>
        <small class="text-muted" style="font-size:0.72rem;">
          <template v-if="priceWithIva">= {{ formatCurrency(form.price_list) }} s/IVA</template>
          <template v-else>= {{ formatCurrency(form.price_list_iva) }} c/IVA</template>
        </small>
      </b-form-group>

      <button type="button" class="ppe-breakdown-toggle mb-2" @click="showBreakdown = !showBreakdown">
        <span class="ppe-breakdown-toggle__label">
          <b-icon icon="sliders" class="mr-1" scale="0.9"></b-icon>
          Descuentos y adicionales
        </span>
        <b-icon :icon="showBreakdown ? 'chevron-up' : 'chevron-down'" class="ppe-breakdown-toggle__icon"></b-icon>
      </button>

      <b-collapse :visible="showBreakdown">
        <div class="ppe-pipeline mb-3">

          <!-- Extra fijo inicial -->
          <div class="ppe-pipeline-row">
            <div class="ppe-pipeline-row__label">
              <span class="ppe-pipeline-icon ppe-pipeline-icon--add">+$</span>
              <div>
                <div class="ppe-pipeline-row__name">Extra fijo inicial</div>
                <div class="ppe-pipeline-row__desc">Monto fijo que se suma al precio de lista</div>
              </div>
            </div>
            <div class="ppe-pipeline-row__input">
              <b-input-group prepend="$" size="sm">
                <formatted-number-input v-model="form.additional" @input="onCostChanged" />
              </b-input-group>
            </div>
          </div>

          <!-- Descuentos en cascada -->
          <div class="ppe-pipeline-row ppe-pipeline-row--group">
            <div class="ppe-pipeline-row__label">
              <span class="ppe-pipeline-icon ppe-pipeline-icon--discount">−%</span>
              <div>
                <div class="ppe-pipeline-row__name">Descuentos en cascada</div>
                <div class="ppe-pipeline-row__desc">Cada uno se aplica sobre el resultado del anterior</div>
              </div>
            </div>
            <div class="ppe-pipeline-row__inputs-group">
              <b-input-group size="sm" class="ppe-pipeline-input-item">
                <b-input-group-prepend><b-input-group-text class="ppe-input-label">Dto 1</b-input-group-text></b-input-group-prepend>
                <formatted-number-input v-model="form.percentage_discount" @input="onCostChanged" />
                <b-input-group-append><b-input-group-text>%</b-input-group-text></b-input-group-append>
              </b-input-group>
              <b-input-group size="sm" class="ppe-pipeline-input-item">
                <b-input-group-prepend><b-input-group-text class="ppe-input-label">Dto 2</b-input-group-text></b-input-group-prepend>
                <formatted-number-input v-model="form.percentage_discount_1" @input="onCostChanged" />
                <b-input-group-append><b-input-group-text>%</b-input-group-text></b-input-group-append>
              </b-input-group>
              <b-input-group size="sm" class="ppe-pipeline-input-item">
                <b-input-group-prepend><b-input-group-text class="ppe-input-label">Dto 3</b-input-group-text></b-input-group-prepend>
                <formatted-number-input v-model="form.percentage_discount_2" @input="onCostChanged" />
                <b-input-group-append><b-input-group-text>%</b-input-group-text></b-input-group-append>
              </b-input-group>
            </div>
          </div>

          <!-- Adicionales % -->
          <div class="ppe-pipeline-row ppe-pipeline-row--group">
            <div class="ppe-pipeline-row__label">
              <span class="ppe-pipeline-icon ppe-pipeline-icon--add">+%</span>
              <div>
                <div class="ppe-pipeline-row__name">Adicionales</div>
                <div class="ppe-pipeline-row__desc">Porcentaje que se suma sobre el precio descontado</div>
              </div>
            </div>
            <div class="ppe-pipeline-row__inputs-group">
              <b-input-group size="sm" class="ppe-pipeline-input-item">
                <b-input-group-prepend><b-input-group-text class="ppe-input-label">Adic 1</b-input-group-text></b-input-group-prepend>
                <formatted-number-input v-model="form.additional_percentage" @input="onCostChanged" />
                <b-input-group-append><b-input-group-text>%</b-input-group-text></b-input-group-append>
              </b-input-group>
              <b-input-group size="sm" class="ppe-pipeline-input-item">
                <b-input-group-prepend><b-input-group-text class="ppe-input-label">Adic 2</b-input-group-text></b-input-group-prepend>
                <formatted-number-input v-model="form.additional_percentage_1" @input="onCostChanged" />
                <b-input-group-append><b-input-group-text>%</b-input-group-text></b-input-group-append>
              </b-input-group>
            </div>
          </div>

          <!-- Extra fijo final -->
          <div class="ppe-pipeline-row">
            <div class="ppe-pipeline-row__label">
              <span class="ppe-pipeline-icon ppe-pipeline-icon--add">+$</span>
              <div>
                <div class="ppe-pipeline-row__name">Extra fijo final</div>
                <div class="ppe-pipeline-row__desc">Monto fijo que se suma al precio ya ajustado</div>
              </div>
            </div>
            <div class="ppe-pipeline-row__input">
              <b-input-group prepend="$" size="sm">
                <formatted-number-input v-model="form.additional_1" @input="onCostChanged" />
              </b-input-group>
            </div>
          </div>

        </div>

        <div class="ppe-steps-box mb-3">
          <div class="ppe-steps-row">
            <span class="text-muted">Lista</span>
            <span></span>
            <span>{{ formatCurrency(toNumber(form.price_list)) }}</span>
          </div>
          <div v-for="step in costSteps" :key="step.key" class="ppe-steps-row">
            <span class="text-muted">{{ step.label }}</span>
            <span :class="step.positive ? 'text-success' : 'text-danger'">
              {{ step.positive ? '+' : '−' }} {{ formatCurrency(Math.abs(step.delta)) }}
            </span>
            <span :class="step.positive ? 'text-success' : 'text-danger'">{{ formatCurrency(step.running) }}</span>
          </div>
          <div class="ppe-steps-row ppe-steps-final">
            <strong>Costo s/IVA</strong>
            <span></span>
            <strong>{{ formatCurrency(selectedCost) }}</strong>
          </div>
          <div class="ppe-steps-row ppe-steps-final ppe-steps-iva">
            <strong>Costo c/IVA ({{ ivaAliquot }}%)</strong>
            <span></span>
            <strong>{{ formatCurrency(selectedCostWithIva) }}</strong>
          </div>
        </div>
      </b-collapse>

      <div v-if="!showBreakdown" class="ppe-cost-bar mb-3">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="ppe-cost-bar__label ppe-cost-bar__label--iva">Costo c/IVA</span>
          <span class="ppe-cost-bar__value ppe-cost-bar__value--iva">{{ formatCurrency(selectedCostWithIva) }}</span>
        </div>
        <div class="d-flex justify-content-between align-items-center">
          <span class="ppe-cost-bar__label" style="font-size:11px;">Costo s/IVA</span>
          <span class="ppe-cost-bar__value" style="font-size:13px;color:#6c757d;">{{ formatCurrency(selectedCost) }}</span>
        </div>
      </div>

      <div v-if="costDirty" class="d-flex mt-3" style="gap: 0.5rem">
        <b-button variant="dark" class="flex-grow-1"
                  :disabled="!supplierProduct || savingCost || !form.id"
                  @click="saveCost">
          <b-spinner small v-if="savingCost" class="mr-1"></b-spinner>
          Guardar costo de compra
        </b-button>
        <b-button variant="outline-danger"
                  class="ppe-reset-button"
                  :disabled="savingCost"
                  @click="resetCostForm"
                  v-b-tooltip.hover title="Restablecer al precio anterior">
          <b-icon icon="arrow-counterclockwise"></b-icon>
        </b-button>
      </div>

      </div><!-- /ppe-panel costo -->

      <!-- LISTAS DE VENTA -->
      <div class="ppe-panel">
      <div class="ppe-panel-title mb-2">
        <b-icon icon="tags" scale="0.9" class="mr-1"></b-icon>
        <span>Listas de venta</span>
        <b-icon v-if="saleLocked" icon="lock-fill" class="ml-2 text-warning" scale="0.85" v-b-tooltip.hover title="Guardá primero el costo de compra"></b-icon>
      </div>

      <b-alert v-if="!highPrecisionPricing && lists.length && !saleLocked" variant="warning" show class="py-2 mb-3 small">
        <AppIcon name="info-circle" class="mr-1" />El precio final puede variar unos centavos al guardar. Para evitarlo, activá el parámetro del sistema “Conservar precisión en precios de venta” en el módulo Costos.
      </b-alert>

      <b-alert show variant="warning" class="py-2 mb-3" v-if="saleLocked">
        <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
        Guardá el costo de compra antes de editar los precios de venta.
      </b-alert>

      <b-alert show variant="warning" v-if="priceListsReadOnly" class="py-2 mb-2">
        Guardá primero el cambio de condición IVA desde la ficha del producto.
      </b-alert>

      <div v-if="lists.length" :class="{'ppe-lists-locked': saleLocked}">
        <b-card v-for="list in lists" :key="list.price_list_id" no-body class="mb-1 ppe-list-card">
          <b-card-header header-tag="div" class="p-0">
            <div class="ppe-list-header" @click="toggleListExpand(list)">
              <div class="ppe-list-header__left">
                <b-icon :icon="expandedListId === list.price_list_id ? 'chevron-down' : 'chevron-right'"
                        scale="0.8" class="text-muted flex-shrink-0"></b-icon>
                <span class="font-weight-bold">{{ list.name }}</span>
                <span v-if="list.using_default_utility" class="text-muted small">(util. pred.)</span>
              </div>
              <div class="ppe-list-header__right" @click.stop>
                <span class="ppe-list-row__price">{{ formatCurrency(listVisiblePrice(list)) }}</span>
                <b-badge variant="success" v-if="list.saved" class="py-1">OK</b-badge>
                <b-spinner small v-if="list.saving"></b-spinner>
                <b-form-checkbox v-model="list.add" switch size="sm"
                                 :disabled="list.saving || priceListsReadOnly || saleLocked"
                                 @change="onListToggle(list)">
                </b-form-checkbox>
              </div>
            </div>
          </b-card-header>

          <b-collapse :visible="expandedListId === list.price_list_id && !!list.add">
            <b-card-body class="py-2 px-3">
              <b-row>
                <b-col cols="4">
                  <b-form-group label="Utilidad %" label-size="sm" class="mb-2">
                    <formatted-number-input v-model="list.utility" size="sm"
                                            :disabled="!list.add || priceListsReadOnly || saleLocked"
                                            @input="updateSalePrice(list)"
                                            @blur="saveListField(list)" />
                  </b-form-group>
                </b-col>
                <b-col cols="4">
                  <b-form-group label="Descuento %" label-size="sm" class="mb-2">
                    <formatted-number-input v-model="list.desc" size="sm"
                                            :disabled="!list.add || priceListsReadOnly || saleLocked"
                                            @input="updateSalePrice(list)"
                                            @blur="saveListField(list)" />
                  </b-form-group>
                </b-col>
                <b-col cols="4">
                  <b-form-group label="Precio final $" label-size="sm" class="mb-2">
                    <formatted-number-input v-model="list.sale_price_iva" size="sm"
                                            :disabled="!list.add || priceListsReadOnly || saleLocked"
                                            @input="updateUtilityFromSalePrice(list)"
                                            @blur="saveListField(list)" />
                  </b-form-group>
                </b-col>
              </b-row>
              <div class="ppe-steps-box mb-2">
                <div class="ppe-steps-row">
                  <span>Costo</span>
                  <span></span>
                  <span>{{ formatCurrency(selectedCost) }}</span>
                </div>
                <div class="ppe-steps-row">
                  <span>Utilidad {{ effectiveListUtility(list) }}%</span>
                  <span v-if="list.using_default_utility" class="text-muted small">(predeterminada)</span>
                  <span></span>
                </div>
                <div class="ppe-steps-row" v-if="ivaAliquot > 0">
                  <span>Subtotal sin IVA</span>
                  <span></span>
                  <span>{{ formatCurrency(list.sale_price) }}</span>
                </div>
                <div class="ppe-steps-row" v-if="ivaAliquot > 0">
                  <span>IVA {{ ivaAliquot }}%</span>
                  <span class="text-success">+ {{ formatCurrency(list.sale_price_iva - list.sale_price) }}</span>
                  <span>{{ formatCurrency(list.sale_price_iva) }}</span>
                </div>
                <div class="ppe-steps-row ppe-steps-final">
                  <strong>Precio final</strong>
                  <span></span>
                  <strong>{{ formatCurrency(listVisiblePrice(list)) }}</strong>
                </div>
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>

      <b-alert show variant="light" v-else class="py-2">
        No hay listas de precios activas configuradas.
      </b-alert>

      </div><!-- /ppe-panel listas -->

      </template><!-- /v-if="!supplierNotLinked" -->
    </template>
  </b-modal>
</template>

<script>
  import costsServiceAPI from './services'
  import Error from '@/handler/error'
  import Helper from '@/handler/helper'
  import Param from '@/config/parameters'
  import {
    purchaseCostFromList,
    salePriceFromCost,
    utilityFromSalePrice,
    netFromGross,
    effectiveUtility,
    isUsingDefaultUtility,
    roundMoney as pmRoundMoney,
  } from './pricingMath'

  const FormattedNumberInput = {
    props: {
      value: { default: 0 },
      disabled: { type: Boolean, default: false },
      size: { type: String, default: '' },
      placeholder: { type: String, default: '' },
      inputClass: { type: String, default: '' },
    },
    data() {
      return { displayValue: '', focused: false }
    },
    watch: {
      value(val) {
        if (!this.focused) this.displayValue = this.fmt(val)
      }
    },
    created() {
      this.displayValue = this.fmt(this.value)
    },
    methods: {
      fmt(val) {
        const num = parseFloat(val)
        if (isNaN(num)) return ''
        return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 8 }).format(num)
      },
      parse(str) {
        const cleaned = String(str || '').replace(/\./g, '').replace(',', '.')
        const num = parseFloat(cleaned)
        return isNaN(num) ? 0 : num
      },
      onFocus(e) {
        this.focused = true
        const el = e.target
        const num = parseFloat(this.value)
        const raw = isNaN(num) || num === 0 ? '' : String(num).replace('.', ',')
        el.value = raw
        this.displayValue = raw
        this.$nextTick(() => el.select())
      },
      onInput(e) {
        this.displayValue = e.target.value
        this.$emit('input', this.parse(e.target.value))
      },
      onKeydown(e) {
        const allowed = ['Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End']
        if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return
        if (/^\d$/.test(e.key)) return
        if ((e.key === ',' || e.key === '.') && !e.target.value.includes(',')) {
          if (e.key === '.') {
            e.preventDefault()
            const p = e.target.selectionStart
            const v = e.target.value
            const nv = v.slice(0, p) + ',' + v.slice(e.target.selectionEnd)
            e.target.value = nv
            e.target.setSelectionRange(p + 1, p + 1)
            this.displayValue = nv
          }
          return
        }
        if (e.key === '-' && e.target.selectionStart === 0 && !e.target.value.includes('-')) return
        e.preventDefault()
      },
      onBlur(e) {
        this.focused = false
        const num = this.parse(e.target.value)
        this.$emit('input', num)
        this.displayValue = this.fmt(num)
        this.$emit('blur', e)
      },
    },
    render(h) {
      const classes = ['form-control']
      if (this.size) classes.push(`form-control-${this.size}`)
      if (this.inputClass) classes.push(this.inputClass)
      return h('input', {
        domProps: { value: this.displayValue, disabled: this.disabled },
        class: classes,
        attrs: { type: 'text', inputmode: 'decimal', placeholder: this.placeholder,
                 autocomplete: 'off', autocorrect: 'off', autocapitalize: 'off', spellcheck: 'false' },
        on: { focus: this.onFocus, input: this.onInput, keydown: this.onKeydown, blur: this.onBlur }
      })
    }
  }

  export default {
    name: 'ProductPricingEditor',
    components: { FormattedNumberInput },
    props: {
      editSource: {
        type: String,
        default: 'productPricingEditor',
      },
      currency: {
        type: String,
        default: 'ARS',
      },
    },
    data() {
      return {
        active: false,
        highPrecisionPricing: Helper.hasParametersAccess(Param.P75),
        loading: false,
        savingCost: false,
        costDirty: false,
        costSaved: false,
        expandedListId: null,
        originalForm: null,
        showBreakdown: false,
        priceWithIva: false,
        priceListsReadOnly: false,
        productId: 0,
        supplierId: 0,
        product: null,
        supplierProduct: null,
        supplierNotLinked: false,
        linking: false,
        optionsLoaded: false,
        options: {
          price_lists: [],
          iva_conditions: [],
          unit_measurements: [],
        },
        form: {
          id: 0,
          product_id: 0,
          price_list: 0,
          price_list_iva: 0,
          percentage_discount: 0,
          percentage_discount_1: 0,
          percentage_discount_2: 0,
          additional: 0,
          additional_1: 0,
          additional_percentage: 0,
          additional_percentage_1: 0,
          iva_condition_id: 0,
        },
        lists: [],
        originalLists: [],
      }
    },
    mounted() {
      this.ensureOptions()
      this.loadPriceWithIvaPreference()
    },
    computed: {
      computedTitle() {
        if(this.product && this.product.name) return 'Precios — ' + this.product.name
        return 'Editar precios'
      },
      saleLocked() {
        return this.costDirty && !this.costSaved
      },
      selectedCostWithIva() {
        return this.roundMoney(this.selectedCost * (1 + this.ivaAliquot / 100))
      },
      selectedCost() {
        return purchaseCostFromList({
          priceList: this.form.price_list,
          additional: this.form.additional,
          percentageDiscount: this.form.percentage_discount,
          percentageDiscount1: this.form.percentage_discount_1,
          percentageDiscount2: this.form.percentage_discount_2,
          additionalPercentage: this.form.additional_percentage,
          additionalPercentage1: this.form.additional_percentage_1,
          additional1: this.form.additional_1,
        })
      },
      ivaAliquot() {
        var cond = this.options.iva_conditions.find(c => c.id == this.form.iva_condition_id)
        return cond ? this.toNumber(cond.aliquot || cond.percentage) : 0
      },
      costSteps() {
        var steps = []
        var running = this.toNumber(this.form.price_list)

        const step = (key, label, fn) => {
          var prev = running
          running = this.roundMoney(fn(running))
          var delta = Math.abs(running - prev)
          if(delta > 0.001) {
            steps.push({ key, label, positive: running > prev, delta, running })
          }
        }

        step('add',   'Extra fijo inicial', r => r + this.toNumber(this.form.additional))
        step('d1',    'Descuento 1',        r => r * (1 - this.toNumber(this.form.percentage_discount) / 100))
        step('d2',    'Descuento 2',        r => r * (1 - this.toNumber(this.form.percentage_discount_1) / 100))
        step('d3',    'Descuento 3',        r => r * (1 - this.toNumber(this.form.percentage_discount_2) / 100))
        step('a1',    'Adicional 1',        r => r * (1 + this.toNumber(this.form.additional_percentage) / 100))
        step('a2',    'Adicional 2',        r => r * (1 + this.toNumber(this.form.additional_percentage_1) / 100))
        step('add1',  'Extra fijo final',   r => r + this.toNumber(this.form.additional_1))

        return steps
      },
    },
    methods: {
      open({ productId, supplierId = 0 } = {}) {
        this.productId = productId
        this.supplierId = supplierId
        this.active = true
        this.loading = true
        this.product = null
        this.supplierProduct = null
        this.lists = []
        this.originalLists = []
        this.showBreakdown = false
        this.savingCost = false
        this.costDirty = false
        this.costSaved = false
        this.expandedListId = null
        this.priceListsReadOnly = false

        Promise.all([
          this.ensureOptions(),
          costsServiceAPI.cargarProductoCostosPorId({ product_id: productId }),
        ]).then(([, response]) => {
          var data = response.data || {}
          this.product = data.product || null
          var suppliersProducts = data.suppliers_products || []
          this.supplierProduct = this.findSupplierProduct(suppliersProducts)
          this.applyForm(this.product, this.supplierProduct)
          this.originalForm = JSON.parse(JSON.stringify(this.form))
          this.lists = this.buildPriceLists(this.product)
          this.originalLists = JSON.parse(JSON.stringify(this.lists))
          this.recalculateSalePrices()
          this.loading = false
        }).catch(error => {
          this.loading = false
          this.$awn.alert(Error.showError(error))
        })
      },
      handleOk() {
        if(this.costDirty) {
          this.$awn.warning('Guardá el costo de compra antes de cerrar')
          return
        }
        this.active = false
      },
      onHidden() {
        this.$emit('editor-closed')
      },
      ensureOptions() {
        if(this.optionsLoaded) return Promise.resolve()
        return Promise.all([
          costsServiceAPI.obtenerOpcionesProductoCostosPorCodigoBarras(),
          costsServiceAPI.obtenerCondicionesIva(),
        ]).then(([optResponse, ivaResponse]) => {
          var data = optResponse.data || {}
          this.options.price_lists = data.price_lists || []
          this.options.unit_measurements = data.unit_measurements || []
          this.options.iva_conditions = Array.isArray(ivaResponse.data) ? ivaResponse.data : []
          this.optionsLoaded = true
        })
      },
      loadProduct() {
        return Promise.all([
          this.ensureOptions(),
          costsServiceAPI.cargarProductoCostosPorId({ product_id: this.productId }),
        ]).then(([, response]) => {
          var data = response.data || {}
          this.product = data.product || null
          var suppliersProducts = data.suppliers_products || []
          this.supplierProduct = this.findSupplierProduct(suppliersProducts)
          this.applyForm(this.product, this.supplierProduct)
          this.originalForm = JSON.parse(JSON.stringify(this.form))
          this.lists = this.buildPriceLists(this.product)
          this.originalLists = JSON.parse(JSON.stringify(this.lists))
          this.recalculateSalePrices()
        })
      },
      findSupplierProduct(items) {
        if(!Array.isArray(items) || !items.length) {
          this.supplierNotLinked = !!this.supplierId
          return null
        }
        if(this.supplierId) {
          var match = items.find(row => row.suppliers_id == this.supplierId)
          if(match) {
            this.supplierNotLinked = false
            return match
          }
          this.supplierNotLinked = true
          return null
        }
        this.supplierNotLinked = false
        var def = items.find(row => row.default)
        return def || items[0]
      },
      linkSupplierToProduct() {
        if(!this.product || !this.supplierId || this.linking) return

        var product = this.product
        // Lumen serializa relaciones en snake_case — igual que en crudProductSuppliersByBarcode
        var subcatObj = product.subcategory || product.Subcategory || null
        var catObj = subcatObj ? (subcatObj.category || subcatObj.Category || null) : null
        var categoryName = catObj ? catObj.name : ''
        var subcategoryName = subcatObj ? subcatObj.name : ''
        if(!categoryName) categoryName = product.name || 'General'
        if(!subcategoryName) subcategoryName = product.name || 'General'

        var supplierCode = product.barcode || product.code || String(product.id)
        var unitId = product.unit_measurement_purchase_id
          || (product.UnitMeasurementPurchase ? product.UnitMeasurementPurchase.id : 0)
          || (product.unit_measurement_purchase ? product.unit_measurement_purchase.id : 0)
          || (this.options.unit_measurements.length ? this.options.unit_measurements[0].id : 0)

        var payload = {
          type: 'NUEVO',
          id: 0,
          products_id: product.id,
          suppliers_id: this.supplierId,
          code: supplierCode,
          code_system: product.code || product.barcode || supplierCode,
          barcode: product.barcode || '',
          name: product.name || '',
          name_short: product.name_short || product.name || '',
          category: categoryName,
          subcategory: subcategoryName,
          line: product.line || '',
          subline: product.subline || '',
          depth: 0,
          long: 0,
          height: 0,
          weight: 0,
          unit_measurement_purchase_id: unitId,
          unit_measurement_sale_id: unitId,
          unit_measurement_sale_conversion: 0,
          iva_condition_id: product.erp_iva_condition_id || 0,
          lists: [],
          price_list: 0,
          percentage_discount: 0,
          percentage_discount_1: 0,
          percentage_discount_2: 0,
          additional: 0,
          additional_1: 0,
          additional_percentage: 0,
          additional_percentage_1: 0,
          skip_supplier_price_update: true,
          edit_source: 'productPricingEditor',
          staff_id: Helper.getStaffId(),
        }

        this.linking = true
        costsServiceAPI.vincularProductoProveedor(payload)
          .then(() => {
            this.linking = false
            this.supplierNotLinked = false
            return this.loadProduct()
          })
          .catch(error => {
            this.linking = false
            this.$awn && this.$awn.alert(Error.showError(error))
          })
      },
      applyForm(product, supplierProduct) {
        var src = supplierProduct || {}
        this.form = {
          id: src.id || 0,
          product_id: product ? product.id : 0,
          price_list: this.toNumber(src.price_list || 0),
          percentage_discount: this.toNumber(src.percentage_discount),
          percentage_discount_1: this.toNumber(src.percentage_discount_1),
          percentage_discount_2: this.toNumber(src.percentage_discount_2),
          additional: this.toNumber(src.additional),
          additional_1: this.toNumber(src.additional_1),
          additional_percentage: this.toNumber(src.additional_percentage),
          additional_percentage_1: this.toNumber(src.additional_percentage_1),
          iva_condition_id: (product && product.erp_iva_condition_id)
            ? product.erp_iva_condition_id
            : (src.iva_condition || 0),
        }
      },
      buildPriceLists(product) {
        var priceMap = {}
        if(product && Array.isArray(product.prices)) {
          product.prices.forEach(p => { priceMap[p.price_list_id] = p })
        }
        return this.options.price_lists.map(list => {
          var price = priceMap[list.id]
          var productUtility = price ? this.toNumber(price.utility) : 0
          var defaultUtility = this.toNumber(list.utility)
          return {
            id: price ? price.id : 0,
            add: !!price,
            price_list_id: list.id,
            name: list.name,
            utility: productUtility,
            default_utility: defaultUtility,
            using_default_utility: productUtility === 0 && defaultUtility > 0,
            desc: price ? this.toNumber(price.desc) : 0,
            sale_price: this.toNumber(price ? price.amount : 0),
            sale_price_iva: this.toNumber(price ? (price.amount_total || price.amount) : 0),
            saving: false,
            saved: false,
          }
        })
      },
      resetCostForm() {
        if(!this.originalForm) return
        this.form = JSON.parse(JSON.stringify(this.originalForm))
        this.costDirty = false
        this.costSaved = false
        this.recalculateSalePrices()
      },
      toggleListExpand(list) {
        this.expandedListId = this.expandedListId === list.price_list_id ? null : list.price_list_id
      },
      onCostChanged() {
        this.costDirty = true
        this.costSaved = false
        this.recalculateSalePrices()
        this.$emit('cost-changed', { priceCost: this.selectedCost })
      },
      saveCost() {
        if(!this.supplierProduct || !this.form.id) return
        this.savingCost = true
        costsServiceAPI.updateProducto({
          id: this.form.id,
          product_id: this.form.product_id,
          price_list: this.form.price_list,
          percentage_discount: this.form.percentage_discount,
          percentage_discount_1: this.form.percentage_discount_1,
          percentage_discount_2: this.form.percentage_discount_2,
          additional: this.form.additional,
          additional_1: this.form.additional_1,
          additional_percentage: this.form.additional_percentage,
          additional_percentage_1: this.form.additional_percentage_1,
          force_price_update: true,
          edit_source: this.editSource,
          staff_id: Helper.getStaffId(),
        }).then(() => {
          this.savingCost = false
          this.costSaved = true
          this.costDirty = false
          this.applySavedCost()
          this.$awn.success('Costo actualizado')
          this.$emit('cost-saved', {
            priceCost: this.selectedCost,
            supplierProduct: this.supplierProduct,
            prices: this.buildPricesSnapshot(),
            productId: this.form.product_id,
          })
        }).catch(error => {
          this.savingCost = false
          this.$awn.alert(Error.showError(error))
        })
      },
      applySavedCost() {
        if(!this.supplierProduct) return
        this.supplierProduct.price_list = this.toNumber(this.form.price_list)
        this.supplierProduct.percentage_discount = this.toNumber(this.form.percentage_discount)
        this.supplierProduct.percentage_discount_1 = this.toNumber(this.form.percentage_discount_1)
        this.supplierProduct.percentage_discount_2 = this.toNumber(this.form.percentage_discount_2)
        this.supplierProduct.additional = this.toNumber(this.form.additional)
        this.supplierProduct.additional_1 = this.toNumber(this.form.additional_1)
        this.supplierProduct.additional_percentage = this.toNumber(this.form.additional_percentage)
        this.supplierProduct.additional_percentage_1 = this.toNumber(this.form.additional_percentage_1)
        this.supplierProduct.price_cost = this.selectedCost
      },
      recalculateSalePrices() {
        this.syncPriceListIva()
        this.lists.forEach(list => this.updateSalePrice(list))
      },
      syncPriceListIva() {
        this.form.price_list_iva = this.roundMoney(this.toNumber(this.form.price_list) * (1 + this.ivaAliquot / 100))
      },
      onPriceListIvaInput() {
        const ivaFactor = 1 + this.ivaAliquot / 100
        if (ivaFactor > 0) {
          this.form.price_list = this.roundMoney(this.toNumber(this.form.price_list_iva) / ivaFactor)
        }
        this.onCostChanged()
      },
      onPriceWithIvaToggle() {
        this.syncPriceListIva()
        this.savePriceWithIvaPreference()
      },
      loadPriceWithIvaPreference() {
        try {
          const saved = localStorage.getItem('bcsp_prefs')
          if (!saved) return
          const parsed = JSON.parse(saved)
          if (parsed && typeof parsed.priceWithIva === 'boolean') {
            this.priceWithIva = parsed.priceWithIva
          }
        } catch (e) { /* localStorage no disponible */ }
      },
      savePriceWithIvaPreference() {
        try {
          const saved = localStorage.getItem('bcsp_prefs')
          const parsed = saved ? (JSON.parse(saved) || {}) : {}
          parsed.priceWithIva = this.priceWithIva
          localStorage.setItem('bcsp_prefs', JSON.stringify(parsed))
        } catch (e) { /* localStorage no disponible */ }
      },
      updateSalePrice(list) {
        this.refreshListUtilitySource(list)
        var { salePrice, salePriceIva } = salePriceFromCost({
          cost: this.selectedCost,
          utility: this.effectiveListUtility(list),
          discount: list.desc,
          ivaAliquot: this.ivaAliquot,
        })
        list.sale_price = salePrice
        list.sale_price_iva = salePriceIva
      },
      updateUtilityFromSalePrice(list) {
        var cost = this.selectedCost
        var saleIva = this.toNumber(list.sale_price_iva)
        var utility = utilityFromSalePrice({
          cost: cost,
          salePrice: saleIva,
          discount: list.desc,
          ivaAliquot: this.ivaAliquot,
        })
        if(utility === null) {
          list.utility = 0
          this.refreshListUtilitySource(list)
          return
        }
        list.sale_price = this.roundMoney(netFromGross(saleIva, this.ivaAliquot))
        list.utility = utility
        this.refreshListUtilitySource(list)
      },
      effectiveListUtility(list) {
        return effectiveUtility({
          utility: list.utility,
          defaultUtility: list.default_utility,
        })
      },
      refreshListUtilitySource(list) {
        list.using_default_utility = isUsingDefaultUtility({ utility: list.utility, defaultUtility: list.default_utility })
      },
      listVisiblePrice(list) {
        return this.ivaAliquot > 0 ? list.sale_price_iva : list.sale_price
      },
      onListToggle(list) {
        if(this.priceListsReadOnly) {
          list.add = !list.add
          this.$awn.warning('Guardá el cambio de IVA antes de editar las listas de venta')
          return
        }
        list.add ? this.createList(list) : this.deleteList(list)
      },
      createList(list) {
        list.saving = true
        costsServiceAPI.agregarPrecioListaProducto({
          price_list_id: list.price_list_id,
          products_id: this.form.product_id,
          utility: list.utility || list.default_utility,
          desc: list.desc,
          edit_source: this.editSource,
          staff_id: Helper.getStaffId(),
        }).then(response => {
          list.id = response.data && response.data.id ? response.data.id : 0
          list.utility = list.utility || list.default_utility
          list.saving = false
          this.markListSaved(list)
          this.updateProductPricesSnapshot()
          this.$emit('price-updated', this.buildPricesSnapshot())
        }).catch(error => {
          list.add = false
          list.saving = false
          this.$awn.alert(Error.showError(error))
        })
      },
      deleteList(list) {
        if(!list.id) { this.markListSaved(list); return }
        list.saving = true
        costsServiceAPI.eliminarPrecioListaProducto(list.id, {
          edit_source: this.editSource,
          staff_id: Helper.getStaffId(),
        }).then(() => {
          list.id = 0
          list.saving = false
          this.markListSaved(list)
          this.updateProductPricesSnapshot()
          this.$emit('price-updated', this.buildPricesSnapshot())
        }).catch(error => {
          list.add = true
          list.saving = false
          this.$awn.alert(Error.showError(error))
        })
      },
      saveListField(list) {
        if(this.priceListsReadOnly || !list.add || !list.id) return
        if(!this.listHasChanges(list)) return
        list.saving = true
        costsServiceAPI.editarPrecioListaProducto({
          id: list.id,
          utility: list.utility,
          desc: list.desc,
          edit_source: this.editSource,
          staff_id: Helper.getStaffId(),
        }).then(() => {
          list.saving = false
          this.markListSaved(list)
          this.updateProductPricesSnapshot()
          this.$emit('price-updated', this.buildPricesSnapshot())
        }).catch(error => {
          list.saving = false
          this.$awn.alert(Error.showError(error))
        })
      },
      listHasChanges(list) {
        var orig = this.originalLists.find(item => item.price_list_id === list.price_list_id)
        if(!orig) return true
        return Math.abs(this.toNumber(list.utility) - this.toNumber(orig.utility)) > 0.001 ||
               Math.abs(this.toNumber(list.desc) - this.toNumber(orig.desc)) > 0.001
      },
      markListSaved(list) {
        var orig = this.originalLists.find(item => item.price_list_id === list.price_list_id)
        if(!orig) {
          this.originalLists.push(JSON.parse(JSON.stringify(list)))
        } else {
          Object.assign(orig, { id: list.id, add: list.add, utility: list.utility, desc: list.desc, sale_price: list.sale_price, sale_price_iva: list.sale_price_iva })
        }
        list.saved = true
        setTimeout(() => { if(list) list.saved = false }, 2200)
      },
      updateProductPricesSnapshot() {
        if(this.product) {
          this.$set(this.product, 'prices', this.buildPricesSnapshot())
        }
      },
      buildPricesSnapshot() {
        return this.lists.filter(l => l.add && l.id).map(l => ({
          id: l.id,
          price_list_id: l.price_list_id,
          products_id: this.form.product_id,
          name: l.name,
          utility: l.utility,
          default_utility: l.default_utility,
          desc: l.desc,
          amount: l.sale_price,
          amount_total: l.sale_price_iva,
        }))
      },
      toNumber(value) {
        var n = parseFloat(value)
        return isNaN(n) ? 0 : n
      },
      roundMoney(value) {
        return pmRoundMoney(value)
      },
      roundUtility(value) {
        return Math.round(this.toNumber(value) * 1e8) / 1e8
      },
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: this.currency }).format(parseFloat(value || 0))
      },
    },
  }
</script>

<style scoped>
  .ppe-product-bar {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
  }
  .ppe-link-alert__body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .ppe-supplier-bar {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
  .ppe-panel {
    border: 1px solid #e4e7ea;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 12px;
  }
  .ppe-panel-title {
    display: flex;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6c757d;
  }
  .ppe-saved-badge {
    font-size: 10px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
  }
  .ppe-breakdown-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 10px;
    background: #f8f9fa;
    border: 1px solid #e4e7ea;
    border-radius: 4px;
    color: #6c757d;
    cursor: pointer;
    text-align: left;
  }
  .ppe-breakdown-toggle:hover {
    background: #f1f3f5;
    border-color: #cfd4da;
  }
  .ppe-breakdown-toggle__label {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #6c757d;
    font-weight: 600;
  }
  .ppe-breakdown-toggle__icon {
    color: #6c757d;
    flex-shrink: 0;
    margin-left: 8px;
  }
  .ppe-steps-box {
    background: #fbfcfd;
    border: 1px solid #e4e7ea;
    border-radius: 4px;
    padding: 8px 12px;
  }
  .ppe-steps-row {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 8px;
    font-size: 12px;
    padding: 3px 0;
    align-items: baseline;
  }
  .ppe-steps-final {
    border-top: 1px solid #d8dbe0;
    margin-top: 4px;
    padding-top: 6px;
    font-size: 13px;
  }
  .ppe-cost-bar {
    padding: 10px 14px;
    background: #f8f9fa;
    border: 1px solid #e4e7ea;
    border-radius: 4px;
  }
  .ppe-cost-bar__label {
    color: #6c757d;
    font-size: 13px;
  }
  .ppe-cost-bar__value {
    font-weight: 700;
    font-size: 16px;
    color: #2f353a;
  }
  .ppe-reset-button {
    border-color: #dc3545;
    color: #dc3545;
    background: #fff5f5;
  }
  .ppe-reset-button:hover:not(:disabled),
  .ppe-reset-button:focus:not(:disabled) {
    color: #fff;
    background: #dc3545;
    border-color: #dc3545;
  }

  /* Listas acordeón */
  .ppe-list-card {
    border: 1px solid #e4e7ea;
  }
  .ppe-list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: #f8f9fa;
    cursor: pointer;
    gap: 8px;
    user-select: none;
  }
  .ppe-list-header:hover {
    background: #eef0f2;
  }
  .ppe-list-header__left {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }
  .ppe-list-header__right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }
  .ppe-list-row__price {
    font-weight: 700;
    color: #2f353a;
    white-space: nowrap;
    font-size: 13px;
  }

  /* Pipeline descuentos/adicionales */
  .ppe-pipeline {
    border: 1px solid #e4e7ea;
    border-radius: 6px;
    overflow: hidden;
  }
  .ppe-pipeline-row {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    gap: 12px;
    border-bottom: 1px solid #e4e7ea;
  }
  .ppe-pipeline-row:last-child {
    border-bottom: none;
  }
  .ppe-pipeline-row--group {
    flex-direction: column;
    align-items: stretch;
  }
  .ppe-pipeline-row__label {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }
  .ppe-pipeline-row--group .ppe-pipeline-row__label {
    margin-bottom: 8px;
  }
  .ppe-pipeline-row__name {
    font-size: 12px;
    font-weight: 600;
    color: #2f353a;
    line-height: 1.3;
  }
  .ppe-pipeline-row__desc {
    font-size: 11px;
    color: #8a93a2;
    line-height: 1.3;
  }
  .ppe-pipeline-row__input {
    width: 160px;
    flex-shrink: 0;
  }
  .ppe-pipeline-row__inputs-group {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .ppe-pipeline-input-item {
    flex: 1;
    min-width: 120px;
  }
  .ppe-pipeline-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
    line-height: 1;
  }
  .ppe-pipeline-icon--add {
    background: #d4edda;
    color: #155724;
  }
  .ppe-pipeline-icon--discount {
    background: #f8d7da;
    color: #721c24;
  }
  .ppe-input-label {
    font-size: 11px;
    min-width: 40px;
    justify-content: center;
  }

  .ppe-iva-toggle {
    font-size: 11px;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    color: #6c757d;
  }
  .ppe-steps-iva {
    color: #1d4ed8;
    border-top: 1.5px solid #bfdbfe !important;
    margin-top: 4px;
  }
  .ppe-cost-bar__label--iva {
    color: #1d4ed8;
    font-weight: 700;
  }
  .ppe-cost-bar__value--iva {
    font-weight: 700;
    font-size: 18px;
    color: #1d4ed8;
  }
  /* Estado bloqueado */
  .ppe-lists-locked {
    opacity: 0.6;
    pointer-events: none;
  }
</style>
